import { CartItems } from '@/hooks/CartContext';
import { HandlePrices } from '@/shared/calculatePrices';
import {
    ClientProps,
    PercentualFornecedorProps,
    PriceProductProps,
    ProductProps,
    TaxSubstitutionProps,
} from '@/types';
import {
    applyComboPriceBenefitToProduct,
    buildComboDisplaySplitsForCartLine,
    ComboCartItemAllocation,
    getComboDiscountParticipatingQuantityForCartItem,
    getComboPriceBenefitForCartItem,
    getSliceAllocationsForCartLine,
} from './comboCartUtils';
import { isActiveComboPortion, lookupFlexRecordWithFallback, resolveEffectiveFlexPercent } from './comboFlexEligibility';

export type DiscountProductLookup = (params: {
    package: string;
    productId: number;
    quantity: number;
    uid?: string;
}) => { percentual?: number; discountPrice?: Record<string, number> } | undefined;

export interface ComboCartLinePriceDeps {
    combos: Array<{ id_campanha?: string; regras_beneficio?: unknown }>;
    isComboCompleteById: (idCampanha: string) => boolean;
    condicoes: number[];
    handlePrices: (
        condicoes: number[],
        product: ProductProps,
        parametroPrecoBase: unknown,
        percentualFornecedor: PercentualFornecedorProps[],
        listSubstTrib: TaxSubstitutionProps[],
        precoPromocao: unknown,
        from: string,
        options?: { skipPaymentAccrual?: boolean }
    ) => PriceProductProps[];
    findPrice: (
        prices: PriceProductProps[],
        option_uid: string,
        paymentCondition: number
    ) => PriceProductProps;
    priceCalcParams: {
        parametroPreco: unknown;
        percentualFornecedor: PercentualFornecedorProps[];
        listSubstTrib: TaxSubstitutionProps[];
        parametroPrecoPromocao: unknown;
    };
    getDiscountProduct?: DiscountProductLookup;
    client?: ClientProps | null;
}

const getUnitPrice = (price?: PriceProductProps | null) =>
    price?.VLR_FINAL ?? price?.VLR_PRECO ?? null;

/** Aplica o percentual flex sobre qualquer preço-base (tabela, combo, VLR_FINAL). */
export const applyFlexPercentToUnitPrice = (
    basePrice: number,
    percentual: number | null | undefined
): number => {
    const base = Number(basePrice);
    const percent = Number(percentual) || 0;
    if (!Number.isFinite(base) || percent === 0) return Number.isFinite(base) ? base : 0;
    return base * (1 + percent / 100);
};

/** Preço flex persistido pelo modal para a condição de pagamento (aceita chave number ou string). */
export const lookupPersistedFlexUnitPrice = (
    discountPrice: Record<string, number> | undefined,
    paymentCondition: number | string
): number | null => {
    if (!discountPrice) return null;

    const raw =
        discountPrice[`${paymentCondition}`] ??
        discountPrice[paymentCondition as keyof typeof discountPrice] ??
        discountPrice[Number(paymentCondition) as unknown as keyof typeof discountPrice];

    if (raw === undefined || raw === null || raw === ('' as unknown as number)) return null;

    const value = Number(raw);
    if (!Number.isFinite(value) || value <= 0) return null;
    return value;
};

export const roundMoney = (value: number): number => {
    if (!Number.isFinite(value)) return 0;
    return Number(value.toFixed(2));
};

const isValidPriceRecord = (price?: PriceProductProps | null): price is PriceProductProps =>
    getUnitPrice(price) != null || price?.VLR_PRECO_CALCULADO != null || price?.ST_VLR_FINAL != null;

const toPriceList = (rawPrecos: ProductProps['PRECO']): PriceProductProps[] => {
    if (!rawPrecos) return [];
    return Array.isArray(rawPrecos) ? rawPrecos : Object.values(rawPrecos);
};

export const applyFlexToComboUnitPrice = (
    comboRecord: PriceProductProps | null | undefined,
    flexPercent: number,
    client: ClientProps | null | undefined,
    listaTributacao: TaxSubstitutionProps[] | undefined
): number | null => {
    if (!flexPercent || !comboRecord?.VLR_PRECO_CALCULADO) return null;

    const precoCalculadoFlexUnit = Number(comboRecord.VLR_PRECO_CALCULADO) * (1 + flexPercent / 100);
    const stFlex = computeStLineTotalLikeStPrice(comboRecord, 1, client, listaTributacao, flexPercent);
    const result = precoCalculadoFlexUnit + stFlex;

    return Number.isFinite(result) ? result : null;
};

/**
 * Preço unitário de combo puro (sem flex) para a linha/porção — só é chamada para porção com combo
 * ATIVO, que por regra de negócio não acumula flex (ver ./comboFlexEligibility).
 */
export const getComboUnitPriceForCartLine = (
    item: Pick<CartItems, 'uid' | 'sku' | 'option_uid'>,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps
): number | null => {
    const { combos, isComboCompleteById, condicoes, handlePrices, findPrice, priceCalcParams } = deps;

    if (!condicoes.length || !allocations?.length || !baseProduct?.COD_PRODUTO) return null;

    const comboBenefit = getComboPriceBenefitForCartItem(
        allocations,
        combos,
        isComboCompleteById,
        { sku: item.sku, option_uid: item.option_uid }
    );

    if (!comboBenefit) return null;

    if (comboBenefit.type === 'PRECO_FIXO') return comboBenefit.fixedPrice;

    const benefitProduct = applyComboPriceBenefitToProduct(baseProduct, comboBenefit, item.option_uid);

    const precos = handlePrices(
        condicoes,
        benefitProduct,
        priceCalcParams.parametroPreco,
        priceCalcParams.percentualFornecedor,
        priceCalcParams.listSubstTrib,
        priceCalcParams.parametroPrecoPromocao,
        'comboCartTotals',
        undefined
    );

    const record = findPrice(precos, item.option_uid, paymentCondition);

    return getUnitPrice(record);
};

const resolveSliceUnitPrice = (
    item: CartItems,
    slice: {
        displayKey?: string;
        comboPortion?: 'combo' | 'excess';
        comboDiscountQuantity?: number;
        comboInstanceId?: string;
    },
    normalUnitPrice: number,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps
): number => {
    // Porção com combo ATIVO ignora qualquer flex — percentual ou preço persistido pelo modal. O
    // dado salvo não é apagado, só deixa de valer enquanto a porção estiver ativa (ver
    // ./comboFlexEligibility).
    if (isActiveComboPortion(slice)) {
        const comboUnit = getComboUnitPriceForCartLine(
            item,
            getSliceAllocationsForCartLine(allocations, slice) ?? allocations,
            baseProduct,
            paymentCondition,
            deps
        );
        if (comboUnit != null) return comboUnit;
        return normalUnitPrice;
    }

    const [packing, qtd] = item.option_uid?.split('_') || [];
    const flexUid = slice.displayKey ?? item.uid;
    const flexEntry = lookupFlexRecordWithFallback(flexUid, item.uid, (uid) =>
        deps.getDiscountProduct?.({
            package: packing,
            productId: item.sku,
            quantity: Number(qtd),
            uid,
        })
    );
    const persisted = lookupPersistedFlexUnitPrice(flexEntry?.discountPrice, paymentCondition);
    if (persisted != null) return persisted;

    const percent = resolveEffectiveFlexPercent(flexEntry?.percentual, slice);
    if (percent !== 0) {
        const normalRecord = getNormalPriceRecordForCartLine(item, baseProduct, paymentCondition, deps);
        const withFlex = applyFlexToComboUnitPrice(
            normalRecord,
            percent,
            deps.client,
            deps.priceCalcParams.listSubstTrib
        );
        if (withFlex != null) return withFlex;
        return applyFlexPercentToUnitPrice(normalUnitPrice, percent);
    }

    return normalUnitPrice;
};

/**
 * Total da linha do carrinho com combo e flex.
 * Usa a mesma chave de porção do card (`displayKey`: uid, uid-instance, uid-excess)
 * para o desconto flex persistido — senão o subtotal/total ignoram o flex aplicado.
 */
export const computeCartLineTotalWithComboDiscount = (
    item: CartItems,
    normalUnitPrice: number,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps
): number => {
    const slices = buildComboDisplaySplitsForCartLine(item, allocations);

    return roundMoney(
        slices.reduce((acc, slice) => {
            const qty = slice.displayQuantity || 0;
            if (!qty) return acc;
            const unit = resolveSliceUnitPrice(
                item,
                slice,
                normalUnitPrice,
                allocations,
                baseProduct,
                paymentCondition,
                deps
            );
            return acc + unit * qty;
        }, 0)
    );
};

/** ST da linha, com o percentual flex de cada porção (combo vs excedente). */
export const computeCartItemStTotal = (
    item: CartItems,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps,
    clientForTax: ClientProps | null | undefined,
    listaTributacao: TaxSubstitutionProps[]
): number => {
    const slices = buildComboDisplaySplitsForCartLine(item, allocations);
    const [packing, qtd] = item.option_uid?.split('_') || [];

    const stTotal = slices.reduce((acc, slice) => {
        const qty = slice.displayQuantity || 0;
        if (!qty) return acc;

        const flexUid = slice.displayKey ?? item.uid;
        const percentualFlex = resolveEffectiveFlexPercent(
            lookupFlexRecordWithFallback(flexUid, item.uid, (uid) =>
                deps.getDiscountProduct?.({
                    package: packing,
                    productId: item.sku,
                    quantity: Number(qtd),
                    uid,
                })
            )?.percentual,
            slice
        );

        const record = isActiveComboPortion(slice)
            ? getComboPriceRecordForCartLine(
                  item,
                  getSliceAllocationsForCartLine(allocations, slice) ?? allocations,
                  baseProduct,
                  paymentCondition,
                  deps
              )
            : getNormalPriceRecordForCartLine(item, baseProduct, paymentCondition, deps);

        return acc + computeStLineTotalLikeStPrice(record, qty, clientForTax, listaTributacao, percentualFlex);
    }, 0);

    return Number(stTotal.toFixed(2));
};

export const getNormalPriceRecordForCartLine = (
    item: Pick<CartItems, 'sku' | 'option_uid'>,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps
): PriceProductProps | null => {
    const { condicoes, handlePrices, findPrice, priceCalcParams } = deps;

    const precosFromProduct = toPriceList(baseProduct.PRECO);
    if (precosFromProduct.length) {
        const fromCatalog = findPrice(precosFromProduct, item.option_uid, paymentCondition);
        if (isValidPriceRecord(fromCatalog)) return fromCatalog;
    }

    if (!condicoes.length || !baseProduct?.COD_PRODUTO) return null;

    const precos = handlePrices(
        condicoes,
        baseProduct,
        priceCalcParams.parametroPreco,
        priceCalcParams.percentualFornecedor,
        priceCalcParams.listSubstTrib,
        priceCalcParams.parametroPrecoPromocao,
        'comboCartTotals-normal'
    );

    const found = findPrice(precos, item.option_uid, paymentCondition);
    return isValidPriceRecord(found) ? found : null;
};

export const getComboPriceRecordForCartLine = (
    item: Pick<CartItems, 'uid' | 'sku' | 'option_uid'>,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps
): PriceProductProps | null => {
    const { combos, isComboCompleteById, condicoes, handlePrices, findPrice, priceCalcParams } = deps;

    if (!condicoes.length || !allocations?.length || !baseProduct?.COD_PRODUTO) return null;

    const comboBenefit = getComboPriceBenefitForCartItem(
        allocations,
        combos,
        isComboCompleteById,
        { sku: item.sku, option_uid: item.option_uid }
    );

    if (!comboBenefit) return null;

    const benefitProduct = applyComboPriceBenefitToProduct(baseProduct, comboBenefit, item.option_uid);
    const calcOptions =
        comboBenefit.type === 'PRECO_FIXO' ? { skipPaymentAccrual: true as const } : undefined;

    const precos = handlePrices(
        condicoes,
        benefitProduct,
        priceCalcParams.parametroPreco,
        priceCalcParams.percentualFornecedor,
        priceCalcParams.listSubstTrib,
        priceCalcParams.parametroPrecoPromocao,
        'comboCartTotals-combo',
        calcOptions
    );

    const found = findPrice(precos, item.option_uid, paymentCondition);
    if (comboBenefit.type === 'PRECO_FIXO' && isValidPriceRecord(found)) {
        return {
            ...found,
            VLR_PRECO: comboBenefit.fixedPrice,
            VLR_PRECO_CALCULADO: comboBenefit.fixedPrice,
            VLR_FINAL: comboBenefit.fixedPrice + (found.ST_VLR_FINAL ?? 0),
        };
    }

    return isValidPriceRecord(found) ? found : null;
};

export interface CheckoutLineStPricing {
    stLineTotal: number;
    stPerPackaging: number;
}

/** Mesma regra do componente StPrice (fonte de verdade do carrinho). */
export const computeStLineTotalLikeStPrice = (
    priceRecord: PriceProductProps | null | undefined,
    quantity: number,
    client: ClientProps | null | undefined,
    listaTributacao: TaxSubstitutionProps[] | undefined,
    percentualFlex = 0
): number => {
    if (!priceRecord || quantity <= 0) return 0;

    const precoSt = priceRecord.ST_VLR_FINAL ?? 0;
    const precoCalculado = priceRecord.VLR_PRECO_CALCULADO;
    const precoPauta = priceRecord.VLR_PAUTA ?? 0;
    const codTrib = priceRecord.COD_TRIBUTACAO_PRODUTO;
    const tribList = listaTributacao ?? [];

    if (
        typeof percentualFlex === 'number' &&
        percentualFlex !== 0 &&
        client &&
        codTrib &&
        precoCalculado &&
        tribList.length
    ) {
        const precoComFlex = precoCalculado + (precoCalculado / 100) * percentualFlex;
        const tributacao = HandlePrices.getTributacao(client, codTrib, tribList);
        const calculado = HandlePrices.CalculaST(tributacao, precoComFlex * quantity, precoPauta);
        return calculado || precoSt || 0;
    }

    if (!quantity || quantity <= 1) {
        return precoSt || 0;
    }

    if (client && codTrib && precoCalculado && tribList.length) {
        const tributacao = HandlePrices.getTributacao(client, codTrib, tribList);
        const calculado = HandlePrices.CalculaST(tributacao, precoCalculado * quantity, precoPauta);
        return calculado || precoSt || 0;
    }

    return precoSt || 0;
};

/** ST da linha no checkout, alinhado ao carrinho (inclui desconto de combo). */
export const computeCheckoutLineStPricing = (
    item: CartItems,
    allocations: ComboCartItemAllocation[] | undefined,
    baseProduct: ProductProps,
    paymentCondition: number,
    deps: ComboCartLinePriceDeps,
    clientForTax: ClientProps,
    listaTributacao: TaxSubstitutionProps[],
    percentualFlex = 0
): CheckoutLineStPricing => {
    const quantity = item.quantity || 0;
    if (quantity <= 0) {
        return { stLineTotal: 0, stPerPackaging: 0 };
    }

    const normalRecord = getNormalPriceRecordForCartLine(item, baseProduct, paymentCondition, deps);
    if (!normalRecord) {
        return { stLineTotal: 0, stPerPackaging: 0 };
    }

    const discountQty = getComboDiscountParticipatingQuantityForCartItem(item, allocations);
    const comboRecord =
        discountQty > 0
            ? getComboPriceRecordForCartLine(item, allocations, baseProduct, paymentCondition, deps)
            : null;
    const regularQty = Math.max(quantity - discountQty, 0);

    // A parcela de combo ativo não acumula flex; a parcela regular (excedente/kit em andamento) sim.
    // Na prática discountQty>0 já implica regularQty===0, pois os chamadores só passam alocações da
    // porção ativa — isto é rede de segurança para o caso de a função ser chamada com a linha inteira.
    const comboFlexPercent = resolveEffectiveFlexPercent(percentualFlex, {
        comboPortion: 'combo',
        comboDiscountQuantity: discountQty,
    });

    let stLineTotal = 0;

    if (discountQty > 0 && comboRecord) {
        stLineTotal += computeStLineTotalLikeStPrice(
            comboRecord,
            discountQty,
            clientForTax,
            listaTributacao,
            comboFlexPercent
        );
    }

    if (regularQty > 0) {
        stLineTotal += computeStLineTotalLikeStPrice(
            normalRecord,
            regularQty,
            clientForTax,
            listaTributacao,
            percentualFlex
        );
    }

    const primaryIsCombo = discountQty > 0 && !!comboRecord;
    const primaryRecord = primaryIsCombo ? comboRecord : normalRecord;
    const stPerPackaging = computeStLineTotalLikeStPrice(
        primaryRecord,
        1,
        clientForTax,
        listaTributacao,
        primaryIsCombo ? comboFlexPercent : percentualFlex
    );

    return {
        stLineTotal: Number(stLineTotal.toFixed(2)),
        stPerPackaging: Number(stPerPackaging.toFixed(2)),
    };
};

/** Fallback de ST a partir do registro de preço (mesma regra do componente StPrice). */
export const resolveStFromPriceRecord = (
    priceRecord: PriceProductProps | null | undefined,
    quantity: number,
    client: ClientProps,
    listaTributacao: TaxSubstitutionProps[],
    percentualFlex = 0
): CheckoutLineStPricing => {
    const stLineTotal = computeStLineTotalLikeStPrice(
        priceRecord,
        quantity,
        client,
        listaTributacao,
        percentualFlex
    );
    const stPerPackaging = computeStLineTotalLikeStPrice(
        priceRecord,
        1,
        client,
        listaTributacao,
        percentualFlex
    );

    return {
        stLineTotal: Number(stLineTotal.toFixed(2)),
        stPerPackaging: Number(stPerPackaging.toFixed(2)),
    };
};
